import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { FilterModel, IServerSideGetRowsParams } from 'ag-grid-community';

import { CSS_CERTIFIED, EXPORT_ENUMS, SEX } from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { createSemenOptions } from '@/services/semen-service/hooks';
import { mapSemenSortModel } from '@/services/semen-service/sort';
import { SemenDto } from '@/services/semen-service/types';
import { addGridQueryParams } from '@/utils/query';

type IDataSourceV2Props = {
  searchBy: string;
  searchTerm: string;
  ownerId?: string;
  fertSiteId?: string;
  exportId?: number;
  filterActiveSemen?: boolean;
  filterByRanch?: boolean;
  filterBySite?: boolean;
  filterByExport?: boolean;
  skip?: boolean;
};

function transformSemenDtoForGrid(item: SemenDto): unknown {
  return {
    ...item,
    owner: {
      id: item.ownerId,
      name: item.ownerName,
      code: item.ownerCode,
    },
    shipment: item.shipmentName
      ? { shipName: item.shipmentName, needByDate: null }
      : null,
  };
}

function buildSearchParams(
  searchBy: string,
  searchTerm: string,
  ownerId: string | undefined,
  fertSiteId: string | undefined,
  exportId: number | undefined,
  filterActiveSemen: boolean | undefined,
): URLSearchParams {
  const searchParams = new URLSearchParams();

  searchParams.set('stateCode', 'eq:0');
  searchParams.set('quantity', filterActiveSemen ? 'eq:0' : 'gt:0');

  if (ownerId) {
    searchParams.set('ownerId', ownerId);
  }

  if (fertSiteId) {
    searchParams.set('storageLocation.siteId', fertSiteId);
  }

  if (exportId !== undefined) {
    searchParams.set('export', `eq:${exportId}`);
  }

  if (!searchTerm) {
    return searchParams;
  }

  if (searchBy === '') {
    searchParams.set('search', searchTerm);
  } else if (
    searchBy === 'quantity' ||
    searchBy === 'concentration' ||
    searchBy === 'cost'
  ) {
    const num = parseFloat(searchTerm);
    if (!Number.isNaN(num)) {
      searchParams.set(searchBy, `eq:${num}`);
    }
  } else if (searchBy === 'sex') {
    const mapped = SEX[searchTerm as keyof typeof SEX];
    if (mapped !== undefined) {
      searchParams.set('sex', `eq:${mapped}`);
    } else {
      const num = parseFloat(searchTerm);
      if (!Number.isNaN(num)) {
        searchParams.set('sex', `eq:${num}`);
      }
    }
  } else if (searchBy === 'cssCertified') {
    const mapped = CSS_CERTIFIED[searchTerm as keyof typeof CSS_CERTIFIED];
    if (mapped !== undefined) {
      searchParams.set('cssCertified', `eq:${mapped}`);
    } else {
      const num = parseFloat(searchTerm);
      if (!Number.isNaN(num)) {
        searchParams.set('cssCertified', `eq:${num}`);
      }
    }
  } else if (searchBy === 'sexed') {
    const bool = searchTerm.toLowerCase();
    if (bool === 'true' || bool === 'false') {
      searchParams.set('sexed', `eq:${bool}`);
    }
  } else if (searchBy === 'export') {
    const mapped = EXPORT_ENUMS[searchTerm as keyof typeof EXPORT_ENUMS];
    if (mapped !== undefined) {
      searchParams.set('export', `eq:${mapped}`);
    }
  } else if (searchBy === 'owner.code') {
    searchParams.set('ownerCode', `contains:${searchTerm}`);
  } else if (searchBy === 'owner.name') {
    searchParams.set('ownerName', `contains:${searchTerm}`);
  } else if (searchBy === 'shipment.shipName') {
    searchParams.set('shipmentName', `contains:${searchTerm}`);
  } else {
    searchParams.set(searchBy, `contains:${searchTerm}`);
  }

  return searchParams;
}

export function useAdvancedSemenDataSourceV2({
  searchBy,
  searchTerm,
  ownerId,
  fertSiteId,
  exportId,
  filterActiveSemen,
  filterByRanch,
  filterBySite,
  filterByExport,
  skip,
}: IDataSourceV2Props) {
  const queryClient = useQueryClient();
  const isFixCustomViews = useFeatureFlag('Fix_Custom_Views');
  const apiRef = useRef<{ api: any } | null>(null);
  const generationRef = useRef(0);

  const filtersRef = useRef({
    searchBy,
    searchTerm,
    ownerId,
    fertSiteId,
    exportId,
    filterActiveSemen,
    skip,
  });
  filtersRef.current = {
    searchBy,
    searchTerm,
    ownerId,
    fertSiteId,
    exportId,
    filterActiveSemen,
    skip,
  };

  const getRows = useCallback(
    ({ request, success, fail, api }: IServerSideGetRowsParams) => {
      const {
        searchBy,
        searchTerm,
        ownerId,
        fertSiteId,
        exportId,
        filterActiveSemen,
        skip,
      } = filtersRef.current;

      if (
        skip ||
        typeof request.startRow !== 'number' ||
        typeof request.endRow !== 'number'
      ) {
        success({ rowData: [], rowCount: 0 });
        return;
      }

      apiRef.current = { api };
      const gen = generationRef.current;

      const searchParams = buildSearchParams(
        searchBy,
        searchTerm,
        ownerId,
        fertSiteId,
        exportId,
        filterActiveSemen,
      );

      const sortModel = isFixCustomViews
        ? mapSemenSortModel(request.sortModel)
        : request.sortModel;

      queryClient
        .fetchQuery({
          ...createSemenOptions(
            addGridQueryParams(
              searchParams,
              request.filterModel as FilterModel | null,
              sortModel,
              request.startRow,
              request.endRow,
            ),
          ),
          staleTime: 30 * 1000,
        })
        .then((response) => {
          if (generationRef.current !== gen) return;
          success({
            rowData: response.items.map(transformSemenDtoForGrid),
            rowCount: response.totalCount,
          });
        })
        .catch(() => {
          if (generationRef.current !== gen) return;
          fail();
        });
    },
    [queryClient, isFixCustomViews],
  );

  const refetch = useCallback(() => {
    if (apiRef.current?.api) {
      generationRef.current++;
      apiRef.current.api.refreshServerSide({ purge: true });
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [
    searchBy,
    searchTerm,
    refetch,
    ownerId,
    fertSiteId,
    exportId,
    filterActiveSemen,
    filterByRanch,
    filterBySite,
    filterByExport,
  ]);

  return useMemo(
    () => ({
      dataSource: { getRows },
      refetch,
    }),
    [getRows, refetch],
  );
}
